import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { generateFromVideo, getActiveJobsCount, listActiveJobs, cancelJob, type Platform } from '../../lib/api';
import { useJobPolling } from '../../hooks/useJobPolling';
import LanguageSelector from '../../components/LanguageSelector';

type TabType = 'upload' | 'youtube';

interface ActiveJob {
  id: string;
  type: string;
  status: string;
  progress: number;
  inputData: any;
  createdAt: string;
  startedAt: string | null;
}

const VIDEO_JOB_STORAGE_KEY = 'growe_video_job_id';
const CONCURRENT_JOB_LIMIT = 3; // Matches backend limit

/**
 * Create content from video upload or YouTube URL
 * Supports async processing with job polling
 * Persists active job across page refreshes
 */
export default function CreateFromVideoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('upload');

  // Form state
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(['linkedin', 'instagram']);
  const [language, setLanguage] = useState('en'); // Default to English, will use workspace default if available
  const [isUploading, setIsUploading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [activeJobsCount, setActiveJobsCount] = useState<number>(0);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelingJobIds, setCancelingJobIds] = useState<Set<string>>(new Set());

  // Restore job from localStorage on mount
  useEffect(() => {
    const savedJobId = localStorage.getItem(VIDEO_JOB_STORAGE_KEY);
    if (savedJobId) {
      setJobId(savedJobId);
    }
  }, []);

  // Fetch active jobs list and count
  useEffect(() => {
    const fetchActiveJobs = async () => {
      try {
        const result = await listActiveJobs('video');
        setActiveJobs(result.jobs);
        setActiveJobsCount(result.count);
      } catch (error) {
        console.error('Failed to fetch active jobs:', error);
      }
    };

    fetchActiveJobs();
    // Poll every 5 seconds to update list
    const interval = setInterval(fetchActiveJobs, 5000);
    return () => clearInterval(interval);
  }, []);

  const platforms: { id: Platform; label: string; icon: string }[] = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
    { id: 'facebook', label: 'Facebook', icon: '👥' },
    { id: 'twitter', label: 'Twitter/X', icon: '🐦' },
    { id: 'youtube', label: 'YouTube', icon: '📹' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  ];

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  // Poll job status
  const { job, isPolling, error: pollingError } = useJobPolling({
    jobId,
    interval: 3000, // Poll every 3 seconds
    maxAttempts: 100, // 5 minutes max
    onComplete: (completedJob) => {
      // Clear persisted job
      localStorage.removeItem(VIDEO_JOB_STORAGE_KEY);
      toast.success(t('create.video.success'));
      navigate(`/${workspaceSlug}/approve`);
    },
    onError: (error) => {
      // Clear persisted job on error
      localStorage.removeItem(VIDEO_JOB_STORAGE_KEY);
      toast.error(error.message || t('create.video.processingError'));
    },
  });

  // Validation
  const isYouTubeUrl = (url: string): boolean => {
    const ytRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return ytRegex.test(url);
  };

  const isFileValid = videoFile !== null && videoFile.type.startsWith('video/');
  const isUrlValid = youtubeUrl.length > 0 && isYouTubeUrl(youtubeUrl);
  const isAtLimit = activeJobsCount >= CONCURRENT_JOB_LIMIT;
  const isValid =
    !isAtLimit &&
    selectedPlatforms.length > 0 &&
    ((activeTab === 'upload' && isFileValid) || (activeTab === 'youtube' && isUrlValid));

  const handleUpload = async () => {
    if (!isValid) return;
    setIsUploading(true);

    try {
      let result;

      if (activeTab === 'upload' && videoFile) {
        // File upload
        result = await generateFromVideo(videoFile, {
          platforms: selectedPlatforms,
          title: title || undefined,
          language: language || undefined, // Falls back to workspace default on backend
        });
      } else if (activeTab === 'youtube' && youtubeUrl) {
        // YouTube URL
        result = await generateFromVideo(youtubeUrl, {
          platforms: selectedPlatforms,
          title: title || undefined,
          language: language || undefined, // Falls back to workspace default on backend
        });
      } else {
        return;
      }

      // Check if async processing (returns jobId)
      if ('jobId' in result) {
        toast.info(t('create.video.processing'));
        // Persist job ID for page refresh recovery
        localStorage.setItem(VIDEO_JOB_STORAGE_KEY, result.jobId);
        setJobId(result.jobId);
      } else {
        // Synchronous result (unlikely for video)
        toast.success(t('create.video.success'));
        navigate(`/${workspaceSlug}/approve`);
      }
    } catch (error: any) {
      console.error('Failed to process video:', error);

      // Handle 429 (rate limit) specifically
      if (error.response?.status === 429) {
        const { activeJobs, limit } = error.response.data;
        toast.error(`You have ${activeJobs} videos being processed. Maximum: ${limit} concurrent jobs.`);
        // Refresh active jobs count
        try {
          const result = await getActiveJobsCount('video');
          setActiveJobsCount(result.count);
        } catch {}
      } else {
        toast.error(error.response?.data?.error || t('create.video.uploadError'));
      }

      setIsUploading(false);
    }
  };

  // Handle job cancellation
  const handleCancelJob = async (jobIdToCancel: string) => {
    setCancelingJobIds(prev => new Set(prev).add(jobIdToCancel));

    try {
      await cancelJob(jobIdToCancel);

      // Refresh active jobs list
      const result = await listActiveJobs('video');
      setActiveJobs(result.jobs);
      setActiveJobsCount(result.count);

      // If canceling current job, clear localStorage
      if (jobIdToCancel === jobId) {
        localStorage.removeItem(VIDEO_JOB_STORAGE_KEY);
        setJobId(null);
        setVideoFile(null);
        setIsUploading(false);
      }

      toast.success(t('create.video.cancelSuccess'));
    } catch (error: any) {
      console.error('Failed to cancel job:', error);
      toast.error(t('create.video.cancelError'));
    } finally {
      setCancelingJobIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobIdToCancel);
        return newSet;
      });
    }
  };

  // File size formatter
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Video duration estimator (rough)
  const estimateProcessingTime = (): string => {
    if (!videoFile) return '?';
    const sizeMB = videoFile.size / (1024 * 1024);
    const estimatedMinutes = Math.ceil(sizeMB / 10); // ~10MB per minute
    return `${estimatedMinutes}-${estimatedMinutes * 2}`;
  };

  // Calculate elapsed time
  const getElapsedTime = (): string => {
    if (!job?.createdAt) return '0:00';
    const elapsed = Math.floor((Date.now() - new Date(job.createdAt).getTime()) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Show processing UI if polling
  if (isPolling || jobId) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">
            {t('create.video.processingTitle')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('create.video.processingSubtitle')}
          </p>
        </div>

        {/* Processing Status Card - Standardized Style */}
        <div className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded">
          <div className="p-6">
            {/* Header with spinner and elapsed time */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Minimal spinner */}
                {job?.status === 'processing' && (
                  <div className="w-4 h-4 border-2 border-gray-900 dark:border-white border-t-transparent animate-spin rounded-full"></div>
                )}
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {job?.status === 'processing'
                    ? t('create.video.analyzing')
                    : t('create.video.queued')}
                </h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {getElapsedTime()}
                </span>
              </div>
            </div>

            {/* Progress message */}
            {job?.statusMessage && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {job.statusMessage}
              </p>
            )}

            {/* Progress bar - h-1 style */}
            <div className="w-full h-1 bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-gray-900 dark:bg-white transition-all duration-500 ease-out"
                style={{ width: `${job?.progress || 0}%` }}
              />
            </div>

            {/* Progress percentage and tokens */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {t('create.video.progress')}
              </span>
              <div className="flex items-center gap-3">
                {job?.tokensUsed !== undefined && job.tokensUsed > 0 && (
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {job.tokensUsed.toLocaleString()} tokens
                  </span>
                )}
                <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                  {job?.progress || 0}%
                </span>
              </div>
            </div>

            {/* Error Message */}
            {pollingError && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                  {t('create.video.error')}: {pollingError.message}
                </p>
              </div>
            )}

            {/* Cancel Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={async () => {
                  if (!jobId) return;

                  try {
                    setIsCanceling(true);

                    // Call API to cancel the job
                    await cancelJob(jobId);

                    // Refresh active jobs count
                    const result = await getActiveJobsCount('video');
                    setActiveJobsCount(result.count);

                    // Clear persisted job and reset state
                    localStorage.removeItem(VIDEO_JOB_STORAGE_KEY);
                    setJobId(null);
                    setVideoFile(null);
                    setIsUploading(false);

                    toast.success(t('create.video.cancelSuccess'));
                  } catch (error: any) {
                    console.error('Failed to cancel job:', error);
                    toast.error(t('create.video.cancelError'));
                  } finally {
                    setIsCanceling(false);
                  }
                }}
                disabled={isCanceling || !jobId}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-2 border-gray-900 dark:border-white rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCanceling ? t('create.video.canceling') : t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show upload UI
  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/${workspaceSlug}/dashboard`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t('create.backToDashboard')}
        </button>

        <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">
          {t('create.video.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t('create.video.subtitle')}
        </p>

        {/* Active Jobs List */}
        {activeJobs.length > 0 && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="font-medium">
                {activeJobsCount} {activeJobsCount === 1 ? 'video' : 'videos'} being processed ({activeJobsCount}/{CONCURRENT_JOB_LIMIT} concurrent jobs)
              </span>
            </div>

            {activeJobs.map((activeJob) => {
              const isCanceling = cancelingJobIds.has(activeJob.id);
              const jobTitle = activeJob.inputData?.title ||
                              activeJob.inputData?.youtubeUrl ||
                              activeJob.inputData?.uploadFilename ||
                              'Video';
              const displayTitle = jobTitle.length > 50 ? jobTitle.substring(0, 50) + '...' : jobTitle;

              return (
                <div
                  key={activeJob.id}
                  className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 rounded p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-3">
                        {activeJob.status === 'processing' && (
                          <div className="w-3 h-3 border-2 border-gray-900 dark:border-white border-t-transparent animate-spin rounded-full"></div>
                        )}
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {displayTitle}
                        </h3>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="w-full h-1 bg-gray-100 dark:border-gray-900 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gray-900 dark:bg-white transition-all duration-500"
                              style={{ width: `${activeJob.progress}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400 tabular-nums">
                          {activeJob.progress}%
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleCancelJob(activeJob.id)}
                      disabled={isCanceling}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-2 border-gray-900 dark:border-white rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isCanceling ? t('create.video.canceling') : t('common.cancel')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* At Limit Warning */}
        {activeJobsCount >= CONCURRENT_JOB_LIMIT && (
          <div className="mt-2 p-3 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded">
            <p className="text-sm text-amber-800 dark:text-amber-300">
              ⚠️ You've reached the maximum of {CONCURRENT_JOB_LIMIT} concurrent video jobs. Please wait for a job to complete before starting a new one.
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex gap-2 border-b-2 border-black dark:border-gray-600">
          <button
            onClick={() => setActiveTab('upload')}
            className={`
              px-6 py-3 font-bold text-lg border-2 border-b-0 border-black dark:border-gray-600 rounded-t-lg transition-all
              ${activeTab === 'upload'
                ? 'bg-black dark:bg-white text-white dark:text-black -mb-[2px]'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            <span className="mr-2">📤</span>
            {t('create.video.tabs.upload')}
          </button>
          <button
            onClick={() => setActiveTab('youtube')}
            className={`
              px-6 py-3 font-bold text-lg border-2 border-b-0 border-black dark:border-gray-600 rounded-t-lg transition-all
              ${activeTab === 'youtube'
                ? 'bg-black dark:bg-white text-white dark:text-black -mb-[2px]'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            <span className="mr-2">📺</span>
            {t('create.video.tabs.youtube')}
          </button>
        </div>
      </div>

      {/* Upload Form */}
      <div className="space-y-6">
        {/* Video Upload Tab */}
        {activeTab === 'upload' && (
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.video.videoLabel')}
            </label>
          <div className="border-2 border-dashed border-black dark:border-gray-600 rounded-lg p-8 text-center">
            {videoFile ? (
              <div className="space-y-4">
                <div className="text-6xl">🎬</div>
                <p className="font-bold text-lg dark:text-gray-100">{videoFile.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatFileSize(videoFile.size)} • {videoFile.type}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {t('create.video.estimatedTime')}: ~{estimateProcessingTime()} {t('create.video.minutes')}
                </p>
                <button
                  onClick={() => setVideoFile(null)}
                  className="px-4 py-2 border-2 border-black dark:border-gray-600 rounded font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-100"
                  disabled={isUploading}
                >
                  {t('create.video.removeVideo')}
                </button>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  className="hidden"
                  disabled={isUploading}
                />
                <div className="text-6xl mb-4">🎥</div>
                <p className="font-bold text-lg mb-2 dark:text-gray-100">
                  {t('create.video.uploadPrompt')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('create.video.supportedFormats')}
                </p>
              </label>
            )}
          </div>
          </div>
        )}

        {/* YouTube URL Tab */}
        {activeTab === 'youtube' && (
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.video.youtubeLabel')}
            </label>
            <input
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder={t('create.video.youtubePlaceholder')}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
              disabled={isUploading}
            />
            <div className="mt-2 flex items-start gap-2">
              {isUrlValid ? (
                <>
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {t('create.video.youtubeValid')}
                  </p>
                </>
              ) : youtubeUrl.length > 0 ? (
                <>
                  <span className="text-red-600 dark:text-red-400 mt-1">✗</span>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {t('create.video.youtubeInvalid')}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('create.video.youtubeHint')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Optional Title */}
        <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
          <label className="block mb-2 font-bold text-lg dark:text-gray-100">
            {t('create.video.titleLabel')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('create.video.titlePlaceholder')}
            className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
            disabled={isUploading}
          />
        </div>

        {/* Language Selection */}
        <LanguageSelector
          value={language}
          onChange={setLanguage}
          label="Content Language"
          description="Choose the language for AI-generated content"
        />

        {/* Platform Selection */}
        <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
          <label className="block mb-4 font-bold text-lg dark:text-gray-100">
            {t('create.idea.platformsLabel')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {platforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => togglePlatform(platform.id)}
                className={`
                  p-4 border-2 rounded-lg font-medium transition-all
                  ${selectedPlatforms.includes(platform.id)
                    ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-brutal'
                    : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-black dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }
                `}
                disabled={isUploading}
              >
                <span className="text-2xl mr-2">{platform.icon}</span>
                {platform.label}
              </button>
            ))}
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={!isValid || isUploading}
          className={`
            w-full py-4 px-6 rounded-lg font-bold text-lg border-2 transition-all
            ${!isValid || isUploading
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 border-gray-400 dark:border-gray-600 cursor-not-allowed'
              : 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white hover:shadow-brutal active:shadow-none'
            }
          `}
        >
          {isUploading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('create.video.uploading')}
            </span>
          ) : (
            t('create.video.uploadButton')
          )}
        </button>
      </div>
    </div>
  );
}
